import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faHouse } from '@fortawesome/pro-light-svg-icons/faHouse';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { faCompactDisc } from '@fortawesome/pro-light-svg-icons/faCompactDisc';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { dayOfIso } from './presaveCreateFacts';
import { formatReleaseDay } from './formatReleaseDay';
import { presaveCreateMessages } from './presaveCreateMessages';
import { PresaveLinkBox } from './PresaveLinkBox';
import { PresaveSongCard } from './PresaveSongCard';
import { CreatedPresave } from './useCreatePresaveFromSong';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveCreatedProps {
  item: CatalogItem;
  page: CreatedPresave;
  services: string[];
  /** The song is the first release of the onboarding checklist: the Home card shows (design b1). */
  isFirstRelease: boolean;
  onOpenStudio: () => void;
  onEditPage: () => void;
  onOpenRelease: () => void;
  onHome: () => void;
  onDone: () => void;
}

interface NextCard {
  key: string;
  icon: IconDefinition;
  title: string;
  body: string;
  button: string;
  onClick: () => void;
}

/**
 * The page is live (design presave-create b1, b2): the link with Copy, Share and Open, and the next
 * steps of the song. After onboarding the kit leads; from a song the release page leads.
 */
const PresaveCreatedComponent: React.FC<PresaveCreatedProps> = ({
  item,
  page,
  services,
  isFirstRelease,
  onOpenStudio,
  onEditPage,
  onOpenRelease,
  onHome,
  onDone,
}) => {
  const intl = useIntl();
  const day = dayOfIso(item.releaseDate);
  const dayText = day === undefined ? undefined : formatReleaseDay(intl, day);

  const cards = useMemo<NextCard[]>(() => {
    const kit: NextCard = {
      key: 'kit',
      icon: faSparkles,
      title: intl.formatMessage(presaveCreateMessages.nextKit),
      body: intl.formatMessage(presaveCreateMessages.nextKitBody),
      button: intl.formatMessage(presaveCreateMessages.nextKitButton),
      onClick: onOpenStudio,
    };
    const edit: NextCard = {
      key: 'edit',
      icon: faPen,
      title: intl.formatMessage(presaveCreateMessages.nextEdit),
      body: intl.formatMessage(presaveCreateMessages.nextEditBody),
      button: intl.formatMessage(presaveCreateMessages.nextEditButton),
      onClick: onEditPage,
    };
    if (isFirstRelease) {
      const home: NextCard = {
        key: 'home',
        icon: faHouse,
        title: intl.formatMessage(presaveCreateMessages.nextHome),
        body: intl.formatMessage(presaveCreateMessages.nextHomeBody),
        button: intl.formatMessage(presaveCreateMessages.nextHomeButton),
        onClick: onHome,
      };
      return [kit, edit, home];
    }
    const release: NextCard = {
      key: 'release',
      icon: faCompactDisc,
      title: intl.formatMessage(presaveCreateMessages.nextRelease),
      body: intl.formatMessage(presaveCreateMessages.nextReleaseBody),
      button: intl.formatMessage(presaveCreateMessages.nextReleaseButton),
      onClick: onOpenRelease,
    };
    return [release, kit, edit];
  }, [intl, isFirstRelease, onEditPage, onHome, onOpenRelease, onOpenStudio]);

  const extraFacts = useMemo(
    () => [{ label: intl.formatMessage(presaveCreateMessages.factServices), value: String(services.length) }],
    [intl, services.length]
  );

  return (
    <div className={styles.body} data-testid="presave-created">
      <div className={styles.qa}>
        <span className={styles.bigOk}>
          <FontAwesomeIcon icon={faCheck} />
        </span>
        <div>
          <h1 className={clsx(styles.question, styles.questionFlush)}>
            {intl.formatMessage(presaveCreateMessages.createdTitle)}
          </h1>
          <p className={styles.sub}>
            {intl.formatMessage(presaveCreateMessages.createdSub, {
              title: item.title,
              date: day === undefined ? '' : formatReleaseDay(intl, day, false),
            })}
          </p>
        </div>
        <PresaveLinkBox shareUrl={page.shareUrl} shareTitle={item.title} />
        <div className={styles.nextLabel}>
          {intl.formatMessage(presaveCreateMessages.nextFor, { title: item.title })}
        </div>
        <div className={styles.nextGrid}>
          {cards.map((card, index) => (
            <div
              key={card.key}
              className={clsx(styles.next, index === 0 && styles.nextPrimary)}
              data-testid={`presave-next-${card.key}`}
            >
              <span className={clsx(styles.kbox, index === 0 && styles.kboxBrand)}>
                <FontAwesomeIcon icon={card.icon} />
              </span>
              <b>{card.title}</b>
              <small>{card.body}</small>
              <SMLButton
                size="sm"
                color={index === 0 ? 'primary' : undefined}
                outline={index !== 0}
                omitCap
                onClick={card.onClick}
              >
                {card.button}
              </SMLButton>
            </div>
          ))}
        </div>
        <div className={styles.actions}>
          <span className={styles.actionsNote}>{intl.formatMessage(presaveCreateMessages.createdFoot)}</span>
          <SMLButton omitCap onClick={onDone} testID="presave-created-done">
            {intl.formatMessage(presaveCreateMessages.done)}
          </SMLButton>
        </div>
      </div>
      <PresaveSongCard item={item} pill="live" dayText={dayText} extraFacts={extraFacts} />
    </div>
  );
};

export const PresaveCreated = TypedMemo(PresaveCreatedComponent);
